import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import moment from 'moment';
import 'moment/locale/es';
import { appConstants } from '../_constants/app.constants';
import { applyBracketMatchShields, applyLeagueMatchShields, bracketMatchHasBothTeams } from './mesaUtils';
import MesaMatchCard from './MesaMatchCard';

const API = appConstants.API_URL;
const NO_FIELD = 'Sin campo asignado';

export default function MesaMultiplePage() {
  const { tournamentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState('');

  useEffect(() => {
    moment.locale('es');
    const cached = sessionStorage.getItem(`mesaTournamentName_${tournamentId}`);
    if (cached) setTitle(cached);
  }, [tournamentId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [lg, fin] = await Promise.all([
          axios.get(`${API}/api/tournamentmatches/${tournamentId}`),
          axios.get(`${API}/api/tournamentmatches/${tournamentId}/bracketmatchs`),
        ]);
        if (cancelled) return;
        const league = (Array.isArray(lg.data) ? lg.data : [])
          .filter((m) => !m.completed)
          .map((m) => ({ kind: 'league', match: applyLeagueMatchShields(m, tournamentId) }));
        const bracket = (Array.isArray(fin.data) ? fin.data : [])
          .map((m) => applyBracketMatchShields(m, tournamentId))
          .filter((m) => !m.completed && bracketMatchHasBothTeams(m))
          .map((m) => ({ kind: 'bracket', match: m }));
        const all = [...league, ...bracket].sort(
          (a, b) => new Date(a.match.dayHour) - new Date(b.match.dayHour),
        );
        setItems(all);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || e.message || 'Error al cargar');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tournamentId]);

  const handleChanged = useCallback((updated) => {
    setItems((prev) => prev.map((it) => (it.match.id === updated.id ? { ...it, match: updated } : it)));
  }, []);

  // El "siguiente" partido: el primero por fecha que aún no está finalizado.
  const nextId = useMemo(() => {
    const pendiente = items.find((it) => !it.match.completed);
    return pendiente ? pendiente.match.id : null;
  }, [items]);

  // Agrupa por campo/pista, manteniendo el orden por fecha dentro de cada grupo.
  const groups = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const field = (it.match.fieldName || '').trim() || NO_FIELD;
      if (!map.has(field)) map.set(field, []);
      map.get(field).push(it);
    }
    return [...map.entries()]
      .sort((a, b) => {
        if (a[0] === NO_FIELD) return 1;
        if (b[0] === NO_FIELD) return -1;
        return a[0].localeCompare(b[0], 'es', { numeric: true });
      })
      .map(([field, list]) => ({ field, list }));
  }, [items]);

  const liveCount = items.filter((it) => it.match.isActive && !it.match.completed).length;
  const pendingCount = items.filter((it) => !it.match.isActive && !it.match.completed).length;

  return (
    <Container maxWidth="md" sx={{ py: 2, px: 2, pb: 8 }}>
      <Button
        component={RouterLink}
        to={`/tournamentactions/${tournamentId}/mesa`}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2, textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
      >
        Volver a la mesa
      </Button>

      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
        <ViewAgendaIcon sx={{ color: '#0f6b5c', fontSize: 30 }} />
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f3531', lineHeight: 1.2 }}>
          Mesa múltiple · {title || 'Torneo'}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Gestiona varios partidos a la vez sin cambiar de pantalla. Cada toque en +/− (o el marcador
        escrito a mano) se guarda al instante.
      </Typography>

      {!loading && !error ? (
        <Stack direction="row" gap={1} sx={{ mb: 2 }} flexWrap="wrap">
          <Chip label={`En juego: ${liveCount}`} color="success" sx={{ fontWeight: 700 }} />
          <Chip label={`Por empezar: ${pendingCount}`} color="warning" sx={{ fontWeight: 700 }} />
        </Stack>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {!loading && !error && items.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No hay partidos por jugar. Todos los partidos están finalizados o aún no tienen los
          equipos definidos.
        </Alert>
      ) : null}

      {!loading && !error && items.length > 0 ? (
        <Stack spacing={3}>
          {groups.map((group) => (
            <Box key={group.field}>
              <Typography
                variant="overline"
                sx={{ fontWeight: 800, color: '#0f6b5c', letterSpacing: '0.06em' }}
              >
                {group.field} ({group.list.length})
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                {group.list.map((it) => (
                  <MesaMatchCard
                    key={it.match.id}
                    match={it.match}
                    kind={it.kind}
                    onChanged={handleChanged}
                    highlighted={it.match.id === nextId}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      ) : null}
    </Container>
  );
}
